import React, { useState, useEffect } from 'react';
import { apiKeyService } from '../../services/apiKeyService';
import { format } from 'date-fns';

const ApiKeyList = () => {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [revokingKeyId, setRevokingKeyId] = useState(null);

    useEffect(() => {
        loadApiKeys();
    }, []);

    // Clear success message after 5 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const loadApiKeys = async () => {
        try {
            setLoading(true);
            const response = await apiKeyService.getApiKeys();
            setKeys(response.data || []);
            setError(null);
        } catch (err) {
            setError('Failed to load API keys: ' + (err.message || 'Unknown error'));
            console.error('Error loading API keys:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeKey = async (keyId, keyName) => {
        if (!window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone and will immediately prevent any further usage of this key.`)) {
            return;
        }

        try {
            setRevokingKeyId(keyId);
            await apiKeyService.revokeApiKey(keyId);

            // Update the key status in the local state
            setKeys(prevKeys =>
                prevKeys.map(key =>
                    key._id === keyId
                        ? { ...key, isActive: false }
                        : key
                )
            );

            setSuccessMessage(`API key "${keyName}" has been successfully revoked.`);
        } catch (err) {
            setError('Failed to revoke API key: ' + (err.message || 'Unknown error'));
            console.error('Error revoking API key:', err);
        } finally {
            setRevokingKeyId(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    };

    if (loading) {
        return <div className="p-4">Loading API keys...</div>;
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">API Keys</h2>
                <button
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                    onClick={() => window.location.href = '/api-keys/new'}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create New API Key
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-300 text-red-600 p-3 rounded mb-4">
                    <div className="flex justify-between">
                        <span>{error}</span>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-700 hover:text-red-900"
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}

            {successMessage && (
                <div className="bg-green-100 border border-green-300 text-green-700 p-3 rounded mb-4">
                    <div className="flex justify-between">
                        <span>{successMessage}</span>
                        <button
                            onClick={() => setSuccessMessage('')}
                            className="text-green-700 hover:text-green-900"
                        >
                            &times;
                        </button>
                    </div>
                </div>
            )}

            {keys.length === 0 ? (
                <div className="text-gray-500">No API keys found.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                                <th className="py-3 px-6 text-left">Name</th>
                                <th className="py-3 px-6 text-left">User</th>
                                <th className="py-3 px-6 text-left">Permissions</th>
                                <th className="py-3 px-6 text-left">Status</th>
                                <th className="py-3 px-6 text-left">Last Used</th>
                                <th className="py-3 px-6 text-left">Expires</th>
                                <th className="py-3 px-6 text-left">Created</th>
                                <th className="py-3 px-6 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-600 text-sm">
                            {keys.map((key) => (
                                <tr key={key._id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="py-3 px-6 text-left whitespace-nowrap">{key.name}</td>
                                    <td className="py-3 px-6 text-left">
                                        {key.userId?.username || key.userId}
                                    </td>
                                    <td className="py-3 px-6 text-left">
                                        {key.permissions.join(', ')}
                                    </td>
                                    <td className="py-3 px-6 text-left">
                                        <span className={`px-2 py-1 rounded-full text-xs ${key.isActive ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                            }`}>
                                            {key.isActive ? 'Active' : 'Revoked'}
                                        </span>
                                    </td>
                                    <td className="py-3 px-6 text-left">{formatDate(key.lastUsed)}</td>
                                    <td className="py-3 px-6 text-left">{formatDate(key.expiresAt)}</td>
                                    <td className="py-3 px-6 text-left">{formatDate(key.createdAt)}</td>
                                    <td className="py-3 px-6 text-center">
                                        <div className="flex item-center justify-center">
                                            <button
                                                className="mr-2 transform hover:text-blue-500 hover:scale-110"
                                                onClick={() => window.location.href = `/api-keys/${key._id}`}
                                                title="View Details"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            </button>
                                            <button
                                                className="mr-2 transform hover:text-blue-500 hover:scale-110"
                                                onClick={() => window.location.href = `/api-keys/${key._id}/edit`}
                                                title="Edit"
                                                disabled={!key.isActive}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke={key.isActive ? "currentColor" : "#ccc"} className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                </svg>
                                            </button>
                                            {key.isActive ? (
                                                <button
                                                    className="transform hover:text-red-500 hover:scale-110"
                                                    onClick={() => handleRevokeKey(key._id, key.name)}
                                                    disabled={revokingKeyId === key._id}
                                                    title="Revoke API Key"
                                                >
                                                    {revokingKeyId === key._id ? (
                                                        <svg className="animate-spin h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    )}
                                                </button>
                                            ) : (
                                                <span className="w-5 h-5 text-gray-300">-</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ApiKeyList; 